import { useRouter } from "next/router";
import Link from "next/link";
import {
  Dispatch,
  FC,
  ReactNode,
  RefAttributes,
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AiOutlineClose, AiOutlineSave } from "react-icons/ai";
import {
  HiOutlineCheckCircle,
  HiOutlineChevronRight,
  HiOutlineExclamation,
  HiOutlineLightningBolt,
  HiOutlinePlus,
  HiOutlineQuestionMarkCircle,
  HiOutlineRefresh,
  HiPencilAlt,
  HiXCircle,
} from "react-icons/hi";
import { useTransition } from "transition-hook";
import Button from "@/components/Button";
import Modal from "@/components/Modal";
import { Policy, DiffPolicy, StaticDiffPolicy } from "@/components/Policy";
import { useSegmentCallback, useSegmentCallbackV2 } from "@/src/segment";
import { EnvData, Option, Fact, TypedId, typedIdsEqual } from "@/src/types";
import Help from "@/components/Help";
import { Tab, TabGroup } from "@/components/Tab";
// todo(nara): Will pick up the work on Wizard later. Keeping the code here for now.
import {
  addLogicFlow,
  confirmClobberFlow,
  showNewTarget,
  Wizard,
  WizardFlowContext,
  WizardFlowProvider,
  wizardRef,
} from "@/components/rules-editor/WorkbenchWizard";
import {
  addCommonRolesAndPermissionsActions,
  capitalize,
  GlobalBlock,
  roleGrantsPermission,
  usePersistedExtraWorkbenchState,
  usePersistedWorkbenchReducer,
  WorkbenchPolicy,
  ADD_GLOBAL_BLOCK_ACTIONS,
  runTestBlock,
  addResourceRolesAndPermissionsTestActions,
  addResourceRelationTestActions,
  roleImpliesRole,
  addResourceGlobalRolesTestActions,
  TestBlockRun,
  WorkbenchPolicyAction,
  workbenchPolicyReducer,
  workbenchToPolicy,
  deleteIdx,
  factToPolicy,
  usePersistedWorkbenchState,
} from "@/src/workbenchLogic";
import { useEnvironmentAction } from "@/src/useAction";
import InlineCode from "@/components/InlineCode";
import FactView from "@/components/FactView";
import { formatDistanceToNow } from "date-fns";
import { FaPlay } from "react-icons/fa";
import { useFactInput } from "@/src/useFactInput";
import Spinner from "@/components/Spinner";
import useData from "@/src/useData";

export const WORKBENCH_INTERACTION_SEGMENT_MESSAGE: string =
  "Workbench Interaction";

const SubmitButton: FC<{
  buttonText?: string;
}> = ({ buttonText }) => {
  return (
    <input
      type="submit"
      value={buttonText ?? "+"}
      className="rounded-r px-2 font-bold text-white shadow-sm bg-[#352990] hover:bg-[#41349e] cursor-pointer"
    />
  );
};

const addTextClasses = "rounded-l pl-1 shadow-sm w-24";

const EditableItem: FC<{
  item: string;
  edit: Dispatch<string>;
  deleteItem?: () => void;
  center?: boolean;
  wide?: boolean;
}> = ({ item, edit, deleteItem, center, wide }) => {
  const itemRef = useRef<HTMLInputElement>(null);
  const [editMode, setEditMode] = useState(false);
  useEffect(() => {
    if (editMode && itemRef.current) {
      itemRef.current.select();
    }
  }, [editMode]);
  const save = () => {
    if (!itemRef.current) return;
    const newItem = itemRef.current.value.trim();
    if (newItem) edit(newItem);
    setEditMode(false);
  };
  return (
    <form
      onSubmit={(e) => {
        save();
        e.preventDefault();
      }}
      className={wide ? "w-full" : "inline-block"}
    >
      <div className="relative">
        <input
          type="text"
          value={editMode ? undefined : item}
          ref={itemRef}
          className={`rounded bg-white ${
            center ? "text-center w-32" : wide ? "w-full " : "w-32 px-2"
          }`}
          contentEditable="true"
          disabled={!editMode}
          onBlur={save}
        />
        {!editMode && (
          <div className="inset-0 absolute flex transition-opacity opacity-0 hover:opacity-100">
            <div
              className="flex items-center cursor-text grow justify-end"
              onClick={setEditMode.bind(null, true)}
            >
              <span className="bg-white">
                <HiPencilAlt />
              </span>
            </div>
            {deleteItem && (
              <div
                className="flex items-center cursor-pointer"
                onClick={deleteItem}
              >
                <span className="bg-white text-red-800">
                  <HiXCircle />
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </form>
  );
};

const Matrix: FC<{
  rows: string[];
  addRow?: (row: string) => void;
  rowPlaceholder?: string;
  cols: string[];
  addCol?: (col: string) => void;
  colPlaceholder?: string;
  matrix: boolean[][];
  setMatrixItem: (row: number, col: number, value: boolean) => void;
  updateRowCallback?: (i: number, newRowHeader: string) => void;
  updateColCallback?: (j: number, newColHeader: string) => void;
  deleteRowCallback?: (i: number) => void;
  deleteColCallback?: (j: number) => void;
  rowsTitle: string;
  colsTitle: string;
  /** indicates if there are other factors that make a checkbox true */
  implied: (i: number, j: number) => boolean;
  wizardRefPrefix: string;
}> = ({
  rows,
  addRow,
  rowPlaceholder,
  cols,
  addCol,
  colPlaceholder,
  matrix,
  setMatrixItem,
  updateRowCallback,
  updateColCallback,
  deleteRowCallback,
  deleteColCallback,
  rowsTitle,
  colsTitle,
  implied,
  wizardRefPrefix,
}) => {
  const addColRef = wizardRef<HTMLInputElement>(`${wizardRefPrefix}.addCol`);
  const addRowRef = wizardRef<HTMLInputElement>(`${wizardRefPrefix}.addRow`);

  if (!Array.isArray(matrix))
    return <>Sorry, something went wrong. Please try refreshing the page.</>;

  return (
    <div className="inline-block">
      <div className="font-bold text-center ml-8">{colsTitle}</div>
      <div className="flex items-center">
        <div className="-rotate-90 origin-bottom-right w-0 h-0 ml-8 flex items-end justify-center font-bold">
          {rowsTitle}
        </div>
        <table className="table-fixed bg-gray-100 rounded-lg">
          <tbody>
            <tr className="font-bold">
              <td className="p-0 w-0"></td>
              {cols.map((col, j) => (
                <td className="px-2 py-1" key={col}>
                  {updateColCallback ? (
                    <EditableItem
                      center
                      item={col}
                      edit={updateColCallback?.bind(null, j)}
                      deleteItem={deleteColCallback?.bind(null, j)}
                    />
                  ) : (
                    col
                  )}
                </td>
              ))}
              {addCol && (
                <td className="w-min px-2 py-1 font-normal">
                  <form
                    onSubmit={(e) => {
                      if (addColRef.current) {
                        addCol(addColRef.current.value);
                        addColRef.current.value = "";
                      }
                      e.preventDefault();
                    }}
                  >
                    <div className="flex w-min">
                      <input
                        type="text"
                        ref={addColRef}
                        placeholder={colPlaceholder ? colPlaceholder : ""}
                        className={addTextClasses}
                      />
                      <SubmitButton />
                    </div>
                  </form>
                </td>
              )}
            </tr>
            {rows.map((perm, i) => (
              <tr key={i}>
                <td className="px-2 py-1 font-bold text-right">
                  {updateRowCallback ? (
                    <EditableItem
                      item={perm}
                      edit={updateRowCallback?.bind(null, i)}
                      deleteItem={deleteRowCallback?.bind(null, i)}
                    />
                  ) : (
                    perm
                  )}
                </td>
                {matrix[i]?.map((checked, j) => (
                  <td className="text-center" key={j}>
                    <input
                      type="checkbox"
                      ref={wizardRef(`${wizardRefPrefix}.${i}.${j}`)}
                      checked={checked || implied(i, j)}
                      onClick={setMatrixItem.bind(null, i, j, !checked)}
                      onChange={setMatrixItem.bind(null, i, j, !checked)}
                      className="cursor-pointer"
                      disabled={implied(i, j) && !checked}
                    />
                  </td>
                ))}
              </tr>
            ))}
            {addRow && (
              <tr>
                <td className="px-2 py-1">
                  <form
                    onSubmit={(e) => {
                      if (addRowRef.current) {
                        addRow(addRowRef.current.value);
                        addRowRef.current.value = "";
                      }
                      e.preventDefault();
                    }}
                  >
                    <div className="flex w-min">
                      <input
                        type="text"
                        ref={addRowRef}
                        placeholder={rowPlaceholder ? rowPlaceholder : ""}
                        className={addTextClasses}
                      />
                      <SubmitButton />
                    </div>
                  </form>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const EditorPanel: FC<
  {
    selectedEnv: EnvData;
    name: ReactNode;
    persistenceKey: string;
  } & RefAttributes<HTMLDivElement>
> = forwardRef(function EditorPanel(
  { selectedEnv, name, persistenceKey, children },
  ref
) {
  const [closedEditorPanels, setClosedEditorPanels] =
    usePersistedExtraWorkbenchState(selectedEnv, "closedEditorPanels", {});
  const isClosed = persistenceKey in closedEditorPanels;
  const { stage } = useTransition(!isClosed, 300);
  return (
    <div className="border rounded p-2 my-4" ref={ref}>
      <div className="flex w-full gap-2">
        <div className="text-2xl font-bold font-space w-full">{name}</div>
        <div
          className={`self-center transition-all rounded-full cursor-pointer ${
            stage === "enter" ? "rotate-90" : "rotate-0"
          } hover:bg-gray-100 text-3xl p-2 -m-2`}
          onClick={
            isClosed
              ? () => {
                  const { [persistenceKey]: _, ...rest } = closedEditorPanels;
                  setClosedEditorPanels(rest);
                }
              : () =>
                  setClosedEditorPanels({
                    ...closedEditorPanels,
                    [persistenceKey]: null,
                  })
          }
          hidden={!children}
        >
          <HiOutlineChevronRight />
        </div>
      </div>
      <div
        className={`overflow-auto transition-all origin-top ${
          stage === "enter"
            ? "max-h-screen opacity-1 "
            : "scale-y-0 max-h-0 opacity-0"
        }`}
      >
        {children}
      </div>
    </div>
  );
});

type ResourceBlockTabType = {
  title: ReactNode;
  contents: ReactNode;
};

const ResourceBlockEditor: FC<{
  selectedEnv: EnvData;
  policy: WorkbenchPolicy;
  resourceIdx: number;
  dispatch: Dispatch<WorkbenchPolicyAction>;
}> = ({ policy, resourceIdx, dispatch, selectedEnv }) => {
  const addRelationRef = useRef<HTMLInputElement>(null);
  const addRelatedResourceRef = useRef<HTMLSelectElement>(null);
  const [selectedTabsState, setSelectedTabs] = usePersistedExtraWorkbenchState(
    selectedEnv,
    "selectedTabs",
    {}
  );
  const { [policy.resources[resourceIdx].name]: selectedTabState } =
    selectedTabsState;
  const setSelectedTab = (tab: number) =>
    setSelectedTabs({
      ...selectedTabsState,
      [policy.resources[resourceIdx].name]: tab,
    });
  const relatedResourceSelectId = useMemo(() => Math.random().toString(), []);
  const relationNameInputId = useMemo(() => Math.random().toString(), []);
  const { pushFlow } = useContext(WizardFlowContext);

  const setCheckbox = (i: number, j: number, value: boolean) => {
    dispatch({
      action: "set perms checkbox",
      resource: resourceIdx,
      i,
      j,
      value,
    });
  };

  const setRoleImplicationCheckbox = (i: number, j: number, value: boolean) => {
    dispatch({
      action: "set roles checkbox",
      resource: resourceIdx,
      i,
      j,
      value,
    });
  };

  const addRole = (role: string) => {
    dispatch({ action: "add role", resource: resourceIdx, role });
  };

  const addPerm = (perm: string) => {
    dispatch({ action: "add perm", resource: resourceIdx, perm });
  };

  const updatePerm = (perm: number, name: string) => {
    dispatch({ action: "edit perm", resource: resourceIdx, perm, name });
  };

  const updateRole = (role: number, name: string) => {
    dispatch({ action: "edit role", resource: resourceIdx, role, name });
  };

  const deleteRole = (role: number) => {
    dispatch({ action: "delete role", resource: resourceIdx, role });
  };

  const deletePerm = (perm: number) => {
    dispatch({ action: "delete perm", resource: resourceIdx, perm });
  };

  const updateResourceName = (resource: number, name: string) => {
    dispatch({
      action: "edit resource name",
      resource,
      name,
    });
  };

  const updateResourceRelationName = (
    resource: number,
    relation_name: string,
    updated_name: string
  ) => {
    dispatch({
      action: "edit resource relation name",
      resource,
      relation_name,
      updated_name,
    });
  };

  const deleteResourceRelation = (resource: number, relation_name: string) => {
    dispatch({
      action: "delete resource relation",
      resource,
      relation_name,
    });
  };

  const updateRelationRole = (
    resourceIdx: number,
    role: number,
    name: string
  ) => {
    dispatch({ action: "edit role", resource: resourceIdx, role, name });
  };

  const deleteRelationRole = (resourceIdx: number, role: number) => {
    dispatch({ action: "delete role", resource: resourceIdx, role });
  };

  const deleteResource = (resource: number) => {
    dispatch({
      action: "delete resource",
      resource,
    });
  };

  const setRelationCheckbox = (
    relation: number,
    i: number,
    j: number,
    value: boolean
  ) => {
    dispatch({
      action: "set relations checkbox",
      resource: resourceIdx,
      relation,
      i,
      j,
      value,
    });
  };

  const resource = policy.resources[resourceIdx];

  const resourceTabs = [
    {
      title: (
        <div className="flex items-center">
          <span>Permissions and Roles</span>
          <Help>A role should grant at least one permission.</Help>
        </div>
      ),
      contents: (
        <>
          <Matrix
            rows={resource.perms}
            rowPlaceholder="permission"
            addRow={addPerm}
            cols={resource.roles}
            colPlaceholder="role"
            addCol={addRole}
            matrix={resource.matrix}
            setMatrixItem={setCheckbox}
            updateRowCallback={updatePerm}
            updateColCallback={updateRole}
            deleteColCallback={deleteRole}
            deleteRowCallback={deletePerm}
            rowsTitle="Permissions"
            colsTitle="Roles"
            implied={(perm, role) => roleGrantsPermission(resource, role, perm)}
            wizardRefPrefix={`resources.${resourceIdx}.rolePermissionMatrix`}
          />
          <div className="flex mt-4 justify-end">
            <Button
              icon={<HiOutlinePlus />}
              medium
              theme={
                policy.tests.some(
                  (test) =>
                    test.name === `${resource.name} roles and permissions`
                )
                  ? undefined
                  : "primary"
              }
              onClick={(e) => {
                e.preventDefault();
                const existingTest = policy.tests.findIndex(
                  (test) =>
                    test.name === `${resource.name} roles and permissions`
                );
                const createTest = () => {
                  dispatch({
                    action: "batch",
                    actions: addResourceRolesAndPermissionsTestActions(
                      policy,
                      resourceIdx
                    ),
                  });
                  showNewTarget(
                    `tests.${
                      existingTest === -1 ? policy.tests.length : existingTest
                    }`
                  );
                };
                if (existingTest === -1) {
                  createTest();
                } else {
                  pushFlow(
                    confirmClobberFlow(
                      `test "${resource.name} roles and permissions"`,
                      createTest
                    )
                  );
                }
              }}
              disabled={policy.actors.length === 0}
            >
              <div
                title={
                  policy.actors.length === 0
                    ? "Add at least one actor type"
                    : ""
                }
              >
                Create Test
              </div>
            </Button>
          </div>
        </>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <span>Role Inheritance</span>
          <Help>A role can grant other roles.</Help>
        </div>
      ),
      contents: (
        <Matrix
          rows={resource.roles}
          cols={resource.roles}
          matrix={resource.rolesImplicationMatrix}
          setMatrixItem={setRoleImplicationCheckbox}
          updateRowCallback={updateRole}
          updateColCallback={updateRole}
          deleteColCallback={deleteRole}
          deleteRowCallback={deleteRole}
          rowsTitle="Roles"
          colsTitle="Roles"
          implied={(i, j) => i === j}
          wizardRefPrefix={`resources.${resourceIdx}.roleInheritanceMatrix`}
        />
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <span>Global Roles</span>
          <Help>
            A global role can grant roles on{" "}
            <InlineCode>{resource.name}</InlineCode> resources.
          </Help>
        </div>
      ),
      contents: policy.global ? (
        <>
          <Matrix
            rows={resource.roles}
            cols={policy.global.roles}
            matrix={resource.globalRolesMatrix}
            setMatrixItem={(i, j, value) =>
              dispatch({
                action: "set resource role global role checkbox",
                resource: resourceIdx,
                i,
                j,
                value,
              })
            }
            updateRowCallback={updateRole}
            updateColCallback={(role, name) =>
              dispatch({ action: "edit global role", role, name })
            }
            deleteRowCallback={deleteRole}
            deleteColCallback={(role) =>
              dispatch({ action: "delete global role", role })
            }
            rowsTitle="Roles"
            colsTitle="Global Roles"
            implied={(role, globalRole) =>
              resource.globalRolesMatrix.some((row, impliedRole) =>
                row.some(
                  (checked, impliedGlobalRole) =>
                    checked &&
                    roleImpliesRole(
                      policy.global!,
                      globalRole,
                      impliedGlobalRole
                    ) &&
                    roleImpliesRole(resource, impliedRole, role)
                )
              )
            }
            wizardRefPrefix={`resources.${resourceIdx}.globalRoleMatrix`}
          />
          <div className="flex mt-4 justify-end">
            <Button
              icon={<HiOutlinePlus />}
              medium
              theme={
                policy.tests.some(
                  (test) => test.name === `${resource.name} global roles`
                )
                  ? undefined
                  : "primary"
              }
              onClick={(e) => {
                e.preventDefault();
                const existingTest = policy.tests.findIndex(
                  (test) => test.name === `${resource.name} global roles`
                );
                const createTest = () => {
                  dispatch({
                    action: "batch",
                    actions: addResourceGlobalRolesTestActions(
                      policy,
                      resourceIdx
                    ),
                  });
                  showNewTarget(
                    `tests.${
                      existingTest === -1 ? policy.tests.length : existingTest
                    }`
                  );
                };
                if (existingTest === -1) {
                  createTest();
                } else {
                  pushFlow(
                    confirmClobberFlow(
                      `test "${resource.name} global roles"`,
                      createTest
                    )
                  );
                }
              }}
              disabled={policy.actors.length === 0}
            >
              <div
                title={
                  policy.actors.length === 0
                    ? "Add at least one actor type"
                    : ""
                }
              >
                Create Test
              </div>
            </Button>
          </div>
        </>
      ) : (
        <div className="m-4">
          <div className="mb-4">
            This policy does not contain any global roles.
          </div>
          <Button
            ref={wizardRef(`resources.${resourceIdx}.tabs.2.buttons.0`)}
            onClick={dispatch.bind(null, {
              action: "batch",
              actions: ADD_GLOBAL_BLOCK_ACTIONS,
            })}
            theme="primary"
            medium
          >
            Add Global Roles
          </Button>
        </div>
      ),
    },
  ];

  const resRelTabs: ResourceBlockTabType[] = resource.relations.map(
    (
      { relation_name, related_resource: relatedResourceName, matrix },
      relationIdx
    ) => {
      const relatedResourceIdx = policy.resources.findIndex(
        ({ name }) => name === relatedResourceName
      );
      const relatedResource = policy.resources[relatedResourceIdx];
      return {
        title: `Relation: ${relation_name}`,
        contents: (
          <div>
            <div className="mb-2 flex justify-between">
              <div>
                Relation{" "}
                <div className="inline-block rounded border">
                  <EditableItem
                    center
                    item={relation_name}
                    edit={updateResourceRelationName?.bind(
                      null,
                      resourceIdx,
                      relation_name
                    )}
                    deleteItem={deleteResourceRelation.bind(
                      null,
                      resourceIdx,
                      relation_name
                    )}
                  />
                </div>{" "}
                to resource <InlineCode>{relatedResourceName}</InlineCode>
              </div>
              <Button
                small
                theme="delete"
                onClick={deleteResourceRelation.bind(
                  null,
                  resourceIdx,
                  relation_name
                )}
              >
                Delete Relation
              </Button>
            </div>

            <div className="flex flex-row whitespace-nowrap font-semibold mb-2 rounded-t border-gray-300">
              <Matrix
                rows={resource.roles}
                cols={relatedResource.roles}
                matrix={matrix}
                updateRowCallback={updateRole}
                deleteRowCallback={deleteRole}
                updateColCallback={updateRelationRole.bind(
                  null,
                  relatedResourceIdx
                )}
                deleteColCallback={deleteRelationRole.bind(
                  null,
                  relatedResourceIdx
                )}
                setMatrixItem={setRelationCheckbox.bind(null, relationIdx)}
                rowsTitle="Roles"
                colsTitle={`Roles on "${relation_name}" ${relatedResourceName}`}
                implied={(role, relatedRole) =>
                  matrix.some((row, impliedRole) =>
                    row.some(
                      (checked, impliedRelatedRole) =>
                        checked &&
                        roleImpliesRole(
                          relatedResource,
                          relatedRole,
                          impliedRelatedRole
                        ) &&
                        roleImpliesRole(resource, impliedRole, role)
                    )
                  )
                }
                wizardRefPrefix={`resources.${resourceIdx}.relations.${relation_name}.matrix`}
              />
            </div>
            <div className="flex mt-4 justify-end">
              <Button
                icon={<HiOutlinePlus />}
                medium
                theme={
                  policy.tests.some(
                    (test) =>
                      test.name === `${resource.name} ${relation_name} relation`
                  )
                    ? undefined
                    : "primary"
                }
                onClick={(e) => {
                  e.preventDefault();
                  const existingTest = policy.tests.findIndex(
                    (test) =>
                      test.name === `${resource.name} ${relation_name} relation`
                  );
                  const createTest = () => {
                    dispatch({
                      action: "batch",
                      actions: addResourceRelationTestActions(
                        policy,
                        resourceIdx,
                        relationIdx
                      ),
                    });
                    showNewTarget(
                      `tests.${
                        existingTest === -1 ? policy.tests.length : existingTest
                      }`
                    );
                  };
                  if (existingTest === -1) {
                    createTest();
                  } else {
                    pushFlow(
                      confirmClobberFlow(
                        `test "${resource.name} ${relation_name} relation"`,
                        createTest
                      )
                    );
                  }
                }}
                disabled={policy.actors.length === 0}
              >
                <div
                  title={
                    policy.actors.length === 0
                      ? "Add at least one actor type"
                      : ""
                  }
                >
                  Create Test
                </div>
              </Button>
            </div>
          </div>
        ),
      };
    }
  );

  const allTabs = [
    ...resourceTabs,
    ...resRelTabs,
    {
      title: (
        <div className="flex items-center">
          <HiOutlinePlus />
        </div>
      ),
      contents: (
        <div className="m-4">
          <Button
            theme="primary"
            block
            icon={<HiOutlineLightningBolt />}
            onClick={() => pushFlow(addLogicFlow(resourceIdx))}
            className="mb-4 -mt-2"
          >
            Add Logic
          </Button>
          <div className="mb-2 flex items-center">
            <span className="font-semibold">Add a relation:</span>
            <Help>
              Sometimes a user{"'"}s permission on a resource depends on who
              they are to a related resource. For example, if you add a relation
              {' "parent" '}
              relating to a Folder, you could use it to say that a user may edit
              a resource if it{"'"}s in a Folder they own.
            </Help>
          </div>
          <form
            onSubmit={(e) => {
              if (addRelationRef.current && addRelatedResourceRef.current) {
                dispatch({
                  action: "add relation",
                  resource: resourceIdx,
                  relation: addRelationRef.current.value,
                  related_resource: addRelatedResourceRef.current.value,
                });
                setSelectedTab(allTabs.length - 1);
                addRelationRef.current.value = "";
                addRelatedResourceRef.current.selectedIndex = 0;
              }
              e.preventDefault();
            }}
            className="flex flex-col space-y-2 w-80"
          >
            <div className="flex items-center">
              <label htmlFor={relationNameInputId}>Relation name:</label>
              <input
                id={relationNameInputId}
                type="text"
                className="border mx-1 px-2 py-1 rounded grow"
                placeholder="e.g. parent"
                ref={addRelationRef}
                required
              />
            </div>
            <div className="flex items-center">
              <label htmlFor={relatedResourceSelectId}>Related resource:</label>
              <select
                id={relatedResourceSelectId}
                className="border mx-1 px-1 py-1 rounded grow"
                ref={addRelatedResourceRef}
                required
              >
                <option value="">(select)</option>
                {policy.resources.map(({ name }, idx) => (
                  <option key={idx} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end">
              <Button type="submit" theme="primary" small>
                Add Relation
              </Button>
            </div>
          </form>
        </div>
      ),
    },
  ];
  const selectedTab = selectedTabState < allTabs.length ? selectedTabState : 0;

  return (
    <EditorPanel
      ref={wizardRef(`resources.${resourceIdx}`)}
      selectedEnv={selectedEnv}
      persistenceKey={resource.name}
      name={
        <div className="flex">
          <div className="mr-2">resource</div>
          <EditableItem
            item={resource.name}
            wide
            edit={updateResourceName?.bind(null, resourceIdx)}
            deleteItem={deleteResource.bind(null, resourceIdx)}
          />
        </div>
      }
    >
      <TabGroup className="!mt-2">
        {allTabs.map(({ title }, i) => (
          <Tab
            ref={wizardRef(`resources.${resourceIdx}.tabs.${i}`)}
            key={i}
            title={title}
            onClick={setSelectedTab.bind(null, i)}
            isSelected={selectedTab === i}
          />
        ))}
      </TabGroup>
      <div className="py-2">{allTabs[selectedTab].contents}</div>

      <div className="m-4"></div>
    </EditorPanel>
  );
};

const GlobalBlockEditor: FC<{
  global: GlobalBlock;
  dispatch: Dispatch<WorkbenchPolicyAction>;
  selectedEnv: EnvData;
}> = ({ global, dispatch, selectedEnv }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const updateRole = (role: number, name: string) =>
    dispatch({ action: "edit global role", role, name });
  const deleteRole = (role: number) =>
    dispatch({ action: "delete global role", role });
  const tabs = [
    {
      title: (
        <div className="flex items-center">
          <span>Permissions and Roles</span>
          <Help>
            A global role is a role an actor has across the entire application.
          </Help>
        </div>
      ),
      contents: (
        <Matrix
          rows={global.perms}
          rowPlaceholder="permission"
          addRow={(perm) =>
            dispatch({
              action: "add global perm",
              perm,
            })
          }
          cols={global.roles}
          colPlaceholder="role"
          addCol={(role) => dispatch({ action: "add global role", role })}
          matrix={global.matrix}
          setMatrixItem={(i, j, value) =>
            dispatch({ action: "set global perms checkbox", i, j, value })
          }
          updateRowCallback={(perm, name) =>
            dispatch({ action: "edit global perm", perm, name })
          }
          updateColCallback={updateRole}
          deleteRowCallback={(perm) =>
            dispatch({ action: "delete global perm", perm })
          }
          deleteColCallback={deleteRole}
          rowsTitle="Permissions"
          colsTitle="Roles"
          implied={(perm, role) => roleGrantsPermission(global, role, perm)}
          wizardRefPrefix={"global.permissionRoleMatrix"}
        />
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <span>Role Inheritance</span>
          <Help>A global role can grant other global roles.</Help>
        </div>
      ),
      contents: (
        <Matrix
          rows={global.roles}
          cols={global.roles}
          matrix={global.rolesImplicationMatrix}
          setMatrixItem={(i, j, value) =>
            dispatch({ action: "set global roles checkbox", i, j, value })
          }
          updateRowCallback={updateRole}
          updateColCallback={updateRole}
          deleteColCallback={deleteRole}
          deleteRowCallback={deleteRole}
          rowsTitle="Roles"
          colsTitle="Roles"
          implied={(i, j) => i === j}
          wizardRefPrefix={"global.roleInheritanceMatrix"}
        />
      ),
    },
  ];
  return (
    <EditorPanel
      selectedEnv={selectedEnv}
      persistenceKey="global"
      ref={wizardRef("global")}
      name={
        <div className="relative">
          global
          <div className="inset-0 absolute flex transition-opacity opacity-0 hover:opacity-100 justify-end">
            <div
              className="flex items-center cursor-pointer"
              onClick={() => dispatch({ action: "delete global block" })}
            >
              <span className="bg-white text-red-800">
                <HiXCircle />
              </span>
            </div>
          </div>
        </div>
      }
    >
      <TabGroup className="!mt-2">
        {tabs.map(({ title }, i) => (
          <Tab
            ref={wizardRef(`global.tabs.${i}`)}
            key={i}
            title={title}
            onClick={setSelectedTab.bind(null, i)}
            isSelected={selectedTab === i}
          />
        ))}
      </TabGroup>
      <div className="py-2">{tabs[selectedTab].contents}</div>
    </EditorPanel>
  );
};

const EditableFactView: FC<{
  fact: Fact;
  edit: (fact: Fact) => void;
  delete_: () => void;
  onArgClick?: (arg: TypedId) => void;
  highlightArg?: TypedId;
  emitErrorMessage: (message: string) => void;
  iconsBgColor: string;
}> = ({
  edit,
  fact,
  onArgClick,
  highlightArg,
  delete_,
  emitErrorMessage,
  iconsBgColor,
}) => {
  const [editMode, setEditMode] = useState(false);
  const { get, FactInput } = useFactInput();
  const save = () => {
    const fact = get();
    if (fact.ok) {
      edit(fact.value);
    } else {
      emitErrorMessage(fact.error);
    }
    setEditMode(false);
  };
  return (
    <div
      className="relative group grow cursor-text"
      onClick={setEditMode.bind(null, true)}
    >
      <div className="m-2">
        <FactView
          fact={fact}
          onArgClick={onArgClick}
          highlightArg={highlightArg}
          compact
        />
      </div>
      {editMode ? (
        <form
          className="inset-0 absolute"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <FactInput
            autoFocus
            onBlur={save}
            defaultValue={factToPolicy(fact)}
            className="font-mono text-sm h-full w-full px-2 py-1"
          />
        </form>
      ) : (
        <div className="right-0 inset-y-0 w-fit absolute transition-opacity opacity-0 group-hover:opacity-100 text-2xl flex items-center">
          <div className={`flex rounded-lg transition ${iconsBgColor}`}>
            <HiPencilAlt />
            <div className="cursor-pointer text-red-800" onClick={delete_}>
              <HiXCircle />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TestBlockEditor: FC<{
  selectedEnv: EnvData;
  policy: WorkbenchPolicy;
  testIdx: number;
  dispatch: Dispatch<WorkbenchPolicyAction>;
  emitErrorMessage: (message: string) => void;
}> = ({ policy, testIdx, dispatch, selectedEnv, emitErrorMessage }) => {
  const { get: getSetupFact, FactInput: SetupFactInput } = useFactInput();
  const { get: getAssertionFact, FactInput: AssertionFactInput } =
    useFactInput();
  const selectAssertTypeRef = useRef<HTMLSelectElement>(null);
  const { name, setup, assertions } = policy.tests[testIdx];
  const canRunTest = setup.length > 0 && assertions.length > 0;
  const [testResults, setTestResults] = useState<
    Option<{
      result: TestBlockRun;
      time: Date;
    }>
  >(null);
  const [testIsRunning, setTestIsRunning] = useState<boolean>(true);
  const [highlightedObject, setHighlightedObject] = useState<
    TypedId | undefined
  >();
  const onArgClick = (arg: TypedId) => {
    if (highlightedObject && typedIdsEqual(arg, highlightedObject)) {
      setHighlightedObject(undefined);
    } else {
      setHighlightedObject(arg);
    }
  };
  const [expandedTargets, setExpandedTargets] = useState<{
    [target: string]: null;
  }>({});
  const segmentCallback = useSegmentCallbackV2();

  /** adds some fake delays for dramatic effect */
  async function updateTestResults(
    time: Date,
    result: TestBlockRun
  ): Promise<void> {
    if (
      result.assertion_messages.every((msg) => msg === undefined) &&
      result.setup_errors.every((msg) => msg === undefined)
    ) {
      // base case
      setTestResults({ result, time });
      return;
    }
    // otherwise,
    // 1. recur without the last message,
    const reducedResult: TestBlockRun = {
      ...result,
      setup_errors: result.setup_errors.slice(),
      assertion_messages: result.assertion_messages.slice(),
    };
    reducedResult.assertion_messages.pop() || reducedResult.setup_errors.pop();
    await updateTestResults(time, reducedResult);
    // 2. now everything but the last message has been shown to the user. wait a bit...
    const ARTIFICIAL_DELAY_MS = 50 + 50 * Math.random();
    await new Promise((resolve) => setTimeout(resolve, ARTIFICIAL_DELAY_MS));
    // 3. add back that last message
    setTestResults({ result, time });
    // 4. then resolve to the caller
  }

  useEffect(() => {
    if (testIsRunning) {
      setExpandedTargets({});
      (async () => {
        const result = await runTestBlock(selectedEnv, policy, testIdx);
        const { setup_errors, assertion_messages, ...resultForTelemetry } =
          result;
        segmentCallback(WORKBENCH_INTERACTION_SEGMENT_MESSAGE, {
          interaction: "run test",
          test: policy.tests[testIdx].name,
          result: resultForTelemetry,
        });
        await updateTestResults(new Date(), result);
        setTestIsRunning(false);
      })();
    }
  }, [testIsRunning]);

  return (
    <EditorPanel
      selectedEnv={selectedEnv}
      /** test names are required to be unique */
      persistenceKey={`tests.${name}`}
      ref={wizardRef(`tests.${testIdx}`)}
      name={
        <div className="flex">
          <div className="mr-2">test</div>
          <EditableItem
            item={name}
            wide
            edit={(name) =>
              dispatch({ action: "edit test block", block: testIdx, name })
            }
            deleteItem={() =>
              dispatch({ action: "delete test block", block: testIdx })
            }
          />
        </div>
      }
    >
      <div className="my-2" ref={wizardRef(`tests.${testIdx}.setup`)}>
        <div className="flex items-center">
          <div className="font-bold">Setup Facts</div>
          <Help>
            These fact &quot;fixtures&quot; will be taken into account when
            evaluating your test assertions.
          </Help>
        </div>
        <div className="flex flex-col min-w-fit">
          {setup.map((fact, factIdx) => {
            const setupFactResult: string | undefined =
              testResults?.result.setup_errors[factIdx];
            const targetId = `tests.${testIdx}.setup.${factIdx}`;
            const bgClasses =
              targetId in expandedTargets
                ? "bg-red-200"
                : setupFactResult === undefined
                ? "bg-white hover:bg-gray-100 group-hover:bg-gray-100"
                : "bg-red-100 hover:bg-red-200 group-hover:bg-red-200";
            return (
              <div
                key={factIdx}
                className={`transition ${bgClasses} relative group flex items-center`}
                ref={wizardRef(targetId)}
              >
                <EditableFactView
                  fact={fact}
                  onArgClick={onArgClick}
                  highlightArg={highlightedObject}
                  emitErrorMessage={emitErrorMessage}
                  edit={(fact) => {
                    dispatch({
                      action: "edit test setup fact",
                      block: testIdx,
                      fact: factIdx,
                      value: fact,
                    });
                  }}
                  delete_={() => {
                    dispatch({
                      action: "delete test setup fact",
                      block: testIdx,
                      fact: factIdx,
                    });
                    if (testResults) {
                      setTestResults({
                        result: {
                          ...testResults.result,
                          setup_errors: deleteIdx(
                            testResults.result.setup_errors,
                            factIdx
                          ),
                        },
                        time: testResults.time,
                      });
                    }
                  }}
                  iconsBgColor={bgClasses}
                />
                {setupFactResult !== undefined && (
                  <div
                    className={`text-xl hover:text-red-700 transition cursor-pointer p-2 rounded-full ${
                      targetId in expandedTargets
                        ? "text-red-700 bg-red-300"
                        : "text-red-600"
                    }`}
                    onClick={() => {
                      if (targetId in expandedTargets) {
                        // it's expanded, so unexpand it
                        const { [targetId]: _, ...updatedExpandedTargets } =
                          expandedTargets;
                        setExpandedTargets(updatedExpandedTargets);
                      } else {
                        // it's not expanded, so expand it
                        setExpandedTargets({
                          ...expandedTargets,
                          [targetId]: null,
                        });
                      }
                    }}
                  >
                    <HiOutlineExclamation />
                  </div>
                )}
                {testResults?.result.setup_errors[factIdx] && (
                  <div
                    className={`transition-opacity group-hover:opacity-100 p-2 rounded-b text-center absolute z-10 top-full font-mono text-sm bg-red-200 text-red-800 pointer-events-none shadow ${
                      targetId in expandedTargets ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    {testResults.result.setup_errors[factIdx]}
                  </div>
                )}
              </div>
            );
          })}
          {setup.length === 0 && (
            <div className="m-2 flex flex-col items-center bg-gray-100 text-gray-700 rounded overflow-x-auto">
              Add at least one setup fact.
            </div>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const fact_result = getSetupFact();
              if (fact_result.ok) {
                dispatch({
                  action: "add test setup fact",
                  block: testIdx,
                  fact: fact_result.value,
                });
              } else {
                emitErrorMessage(
                  `Could not add setup fact: ${fact_result.error}`
                );
              }
            }}
            className="flex gap-2 text-sm"
          >
            <SetupFactInput
              className="grow border px-2 py-1 rounded font-mono"
              placeholder={`has_role(User{"alice"}, "owner", Repository{"anvils"})`}
            />
            <Button type="submit" medium theme="primary">
              Add
            </Button>
          </form>
        </div>
      </div>
      <div className="my-2" ref={wizardRef(`tests.${testIdx}.assertions`)}>
        <div className="flex items-center">
          <div className="font-bold">Assertions</div>
          <Help>
            The test passes if all of its assertions succeed against the current
            policy given the Setup Facts declared above.
          </Help>
        </div>
        <div className="flex flex-col min-w-fit">
          {assertions.map(({ expected, fact }, assertionIdx) => {
            const assertionResult: true | false | undefined =
              testResults?.result.assertion_messages[assertionIdx]?.ok;
            const targetId = `tests.${testIdx}.assertions.${assertionIdx}`;
            const bgClasses =
              targetId in expandedTargets
                ? "bg-red-200"
                : assertionResult === true
                ? "bg-green-100 hover:bg-green-200 group-hover:bg-green-200"
                : assertionResult === false
                ? "bg-red-100 hover:bg-red-200 group-hover:bg-red-200"
                : "bg-white hover:bg-gray-100 group-hover:bg-gray-100";
            return (
              <div
                key={assertionIdx}
                className={`relative transition ${bgClasses} group`}
                ref={wizardRef(targetId)}
              >
                <div className="flex w-full shrink-0 items-center">
                  <select
                    className="font-mono border rounded text-sm bg-transparent mx-1"
                    onChange={(e) => {
                      dispatch({
                        action: "edit test assertion",
                        block: testIdx,
                        assertion: assertionIdx,
                        value: {
                          expected: e.target.value === "assert",
                          fact,
                        },
                      });
                    }}
                  >
                    <option value="assert" selected={expected}>
                      assert
                    </option>
                    <option value="assert_not" selected={!expected}>
                      assert_not
                    </option>
                  </select>
                  <EditableFactView
                    fact={fact}
                    highlightArg={highlightedObject}
                    onArgClick={onArgClick}
                    emitErrorMessage={emitErrorMessage}
                    edit={(fact) => {
                      dispatch({
                        action: "edit test assertion",
                        block: testIdx,
                        assertion: assertionIdx,
                        value: { expected, fact },
                      });
                    }}
                    delete_={() => {
                      dispatch({
                        action: "delete test assertion",
                        block: testIdx,
                        assertion: assertionIdx,
                      });
                      if (testResults) {
                        setTestResults({
                          result: {
                            ...testResults.result,
                            assertion_messages: deleteIdx(
                              testResults.result.assertion_messages,
                              assertionIdx
                            ),
                          },
                          time: testResults.time,
                        });
                      }
                    }}
                    iconsBgColor={bgClasses}
                  />
                  <div
                    className={`text-xl p-2 rounded-full ${
                      targetId in expandedTargets
                        ? "text-red-700 bg-red-300"
                        : assertionResult === true
                        ? "text-green-600"
                        : assertionResult === false
                        ? "text-red-600 cursor-pointer"
                        : "text-gray-600"
                    }`}
                    onClick={() => {
                      if (targetId in expandedTargets) {
                        // it's expanded, so unexpand it
                        const { [targetId]: _, ...updatedExpandedTargets } =
                          expandedTargets;
                        setExpandedTargets(updatedExpandedTargets);
                      } else if (assertionResult === false) {
                        // it's not expanded, so expand it
                        setExpandedTargets({
                          ...expandedTargets,
                          [targetId]: null,
                        });
                      }
                    }}
                  >
                    {assertionResult === true ? (
                      <HiOutlineCheckCircle />
                    ) : assertionResult === false ? (
                      <HiOutlineExclamation />
                    ) : (
                      <Spinner size={20} />
                    )}
                  </div>
                </div>
                {testResults?.result.assertion_messages[assertionIdx]?.ok ===
                  false && (
                  <div
                    className={`absolute transition-opacity group-hover:opacity-100 hover:opacity-100 z-10 top-full w-full rounded-b p-2 text-center font-mono text-sm bg-red-200 text-red-800 pointer-events-none ${
                      targetId in expandedTargets ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    {((message) => !message.ok && message.error)(
                      testResults.result.assertion_messages[assertionIdx]
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {assertions.length === 0 && (
            <div className="m-2 flex flex-col items-center bg-gray-100 text-gray-700 rounded overflow-x-auto">
              Add at least one assertion.
            </div>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const fact_result = getAssertionFact();
              if (fact_result.ok) {
                dispatch({
                  action: "add test assertion",
                  block: testIdx,
                  assertion: {
                    expected: selectAssertTypeRef.current?.value === "assert",
                    fact: fact_result.value,
                  },
                });
                setTestIsRunning(true);
              } else {
                emitErrorMessage(
                  `Could not add assertion: ${fact_result.error}`
                );
              }
            }}
            className="flex gap-2 text-sm"
          >
            <select
              ref={selectAssertTypeRef}
              className="font-mono border rounded pl-1"
            >
              <option value="assert">assert</option>
              <option value="assert_not">assert_not</option>
            </select>
            <AssertionFactInput
              className="grow border px-2 py-1 rounded"
              placeholder={`allow(User{"alice"}, "owner", Repository{"anvils"})`}
            />
            <Button type="submit" medium theme="primary">
              Add
            </Button>
          </form>
        </div>
      </div>
      <div className="mt-4">
        <Button
          theme="primary"
          block
          disabled={!canRunTest}
          icon={<FaPlay />}
          onClick={(e) => {
            e.preventDefault();
            setTestIsRunning(true);
          }}
          loading={testIsRunning}
        >
          Run Test
        </Button>
        <div className="flex justify-end text-sm text-gray-600 h-5">
          {testResults &&
            `Last run: ${formatDistanceToNow(testResults.time)} ago.`}
        </div>
        <div className="flex justify-between items-center mb-2 h-6">
          {testResults &&
            !testIsRunning &&
            (testResults.result.success ? (
              <div className={"font-bold text-green-500"}>Test passed.</div>
            ) : (
              <div className={"font-bold text-red-500"}>
                {testResults.result.passed} of {testResults.result.total}{" "}
                assertions succeeded.
              </div>
            ))}
        </div>
      </div>
    </EditorPanel>
  );
};

const AddToWorkbenchForm: FC<{
  workbenchPolicy: WorkbenchPolicy;
  dispatch: Dispatch<WorkbenchPolicyAction>;
  selectedEnv: EnvData;
}> = ({ dispatch, workbenchPolicy }) => {
  const nameRef = useRef<HTMLInputElement>(null);
  const [selectedType, setSelectedType] = useState<
    "resource" | "global" | "test" | "actor"
  >("resource");
  return (
    <form
      className="flex gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!nameRef.current) {
          return;
        }
        const name = nameRef.current.value;
        nameRef.current.value = "";
        switch (selectedType) {
          case "resource":
            return dispatch({
              action: "batch",
              actions: [
                {
                  action: "add resource",
                  resource: capitalize(name),
                },
                ...addCommonRolesAndPermissionsActions(
                  workbenchPolicy.resources.length
                ),
              ],
            });
          case "global":
            return dispatch({
              action: "batch",
              actions: ADD_GLOBAL_BLOCK_ACTIONS,
            });
          case "test":
            return dispatch({
              action: "add test block",
              name,
              replace: false,
            });
          case "actor":
            return dispatch({
              action: "add actor",
              name,
            });
        }
      }}
    >
      <div className="my-1 flex items-center">
        Add&nbsp;
        <select
          onChange={(e) => {
            setSelectedType(
              e.target.value as "resource" | "global" | "test" | "actor"
            );
          }}
          className="border rounded"
        >
          <option value="resource">a resource</option>
          <option value="actor">an actor</option>
          <option value="global" disabled={!!workbenchPolicy.global}>
            the global block
          </option>
          <option value="test">a test</option>
        </select>
        <Help>
          <div className="mb-2">
            <span className="font-bold">Resources</span> are business objects,
            classes, or models in your application, such as{" "}
            <InlineCode>Document</InlineCode> or{" "}
            <InlineCode>Organization</InlineCode>.
          </div>
          <div className="mb-2">
            An <span className="font-bold">actor</span> is an identity model
            within your application; <InlineCode>User</InlineCode> is the most
            common example.
          </div>
          <div className="mb-2">
            The <span className="font-bold">global block</span> defines
            application-wide roles and permissions.
            {workbenchPolicy.global && (
              <div className="ml-4">
                <span className="font-bold">Note:</span> the global block is
                already defined below.
              </div>
            )}
          </div>
          <div>
            <span className="font-bold">Tests</span> validate that your policy
            is working as expected.
          </div>
        </Help>
      </div>
      <input
        type="text"
        ref={nameRef}
        className="border px-2 py-1 rounded"
        placeholder={
          selectedType === "resource"
            ? "Resource name"
            : selectedType === "test"
            ? "test name"
            : selectedType === "actor"
            ? "Actor name"
            : "N/A"
        }
        {...{ [selectedType === "global" ? "disabled" : "required"]: true }}
      />
      <Button
        type="submit"
        theme="primary"
        medium
        className="px-2 py-1 rounded"
      >
        +
      </Button>
    </form>
  );
};

const ActorBlockEditor: FC<{
  selectedEnv: EnvData;
  policy: WorkbenchPolicy;
  actorIdx: number;
  dispatch: Dispatch<WorkbenchPolicyAction>;
}> = ({ selectedEnv, policy, actorIdx, dispatch }) => {
  const { name } = policy.actors[actorIdx];
  return (
    <EditorPanel
      selectedEnv={selectedEnv}
      /** test names are required to be unique */
      persistenceKey={`actors.${name}`}
      ref={wizardRef(`actors.${actorIdx}`)}
      name={
        <div className="flex">
          <div className="mr-2">actor</div>
          <EditableItem
            item={name}
            wide
            edit={(name) =>
              dispatch({ action: "edit actor name", actor: actorIdx, name })
            }
            deleteItem={() =>
              dispatch({ action: "delete actor", actor: actorIdx })
            }
          />
        </div>
      }
    />
  );
};

const RuleEditor: FC<{
  selectedEnv: EnvData;
  policy: WorkbenchPolicy;
  ruleIdx: number;
  dispatch: Dispatch<WorkbenchPolicyAction>;
}> = ({ selectedEnv, policy, ruleIdx, dispatch }) => {
  const { description, source } = policy.rules[ruleIdx];
  return (
    <EditorPanel
      selectedEnv={selectedEnv}
      persistenceKey={`rules.${ruleIdx}`}
      ref={wizardRef(`rules.${ruleIdx}`)}
      name={
        <div className="flex">
          <div className="mr-2">rule</div>
          <EditableItem
            item={description}
            wide
            edit={(description) =>
              dispatch({
                action: "edit rule description",
                rule: ruleIdx,
                description,
              })
            }
            deleteItem={() =>
              dispatch({ action: "delete rule", rule: ruleIdx })
            }
          />
        </div>
      }
    >
      <Policy policy={source} lines={null} />
    </EditorPanel>
  );
};

const WorkbenchEditor: FC<{
  workbenchPolicy: WorkbenchPolicy;
  dispatch: Dispatch<WorkbenchPolicyAction>;
  selectedEnv: EnvData;
  emitErrorMessage: (message: string) => void;
}> = ({ workbenchPolicy, dispatch, selectedEnv, emitErrorMessage }) => {
  return (
    <>
      <AddToWorkbenchForm
        workbenchPolicy={workbenchPolicy}
        dispatch={dispatch}
        selectedEnv={selectedEnv}
      />
      {workbenchPolicy.actors.map((_actor, i) => (
        <ActorBlockEditor
          key={i}
          selectedEnv={selectedEnv}
          policy={workbenchPolicy}
          actorIdx={i}
          dispatch={dispatch}
        />
      ))}
      {workbenchPolicy.global && (
        <GlobalBlockEditor
          selectedEnv={selectedEnv}
          global={workbenchPolicy.global}
          dispatch={dispatch}
        />
      )}
      {workbenchPolicy.resources.map((_resource, i) => (
        <ResourceBlockEditor
          key={i}
          selectedEnv={selectedEnv}
          policy={workbenchPolicy}
          resourceIdx={i}
          dispatch={dispatch}
        />
      ))}
      {workbenchPolicy.rules.map((_rule, i) => (
        <RuleEditor
          key={i}
          selectedEnv={selectedEnv}
          policy={workbenchPolicy}
          ruleIdx={i}
          dispatch={dispatch}
        />
      ))}
      {workbenchPolicy.tests.map((_, testIdx) => (
        <TestBlockEditor
          key={`test-${testIdx}`}
          selectedEnv={selectedEnv}
          dispatch={dispatch}
          policy={workbenchPolicy}
          testIdx={testIdx}
          emitErrorMessage={emitErrorMessage}
        />
      ))}
    </>
  );
};

const Workbench: FC<{
  selectedEnv: EnvData;
  onUnsavedChangesChange: (unsavedChanges: boolean) => void;
  header?: ReactNode;
  otherToolbarButtons?: ReactNode;
  hideWhenWouldIUse?: boolean;
  fetchFromBackend?: boolean;
  backendStatePrefetched?: boolean;
}> = ({
  selectedEnv,
  onUnsavedChangesChange,
  header,
  otherToolbarButtons,
  hideWhenWouldIUse,
  fetchFromBackend,
  backendStatePrefetched,
}) => {
  const workbenchEditorContainer = useRef<HTMLDivElement>(null);
  const policyContainer = useRef<HTMLDivElement>(null);
  const [showPolicyOverwriteWarning, setShowPolicyOverwriteWarning] =
    useState<boolean>(false);
  const [showWorkbenchStartOverWarning, setShowWorkbenchStartOverWarning] =
    useState<boolean>(false);
  const [limitationsOpen, setLimitationsOpen] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const { stage: errorStage, shouldMount: errorShouldMount } = useTransition(
    showErrorMessage,
    300
  );

  const [isBackendStateLoading, setIsBackendStateLoading] = useState<boolean>(
    !backendStatePrefetched
  );

  /** a number between 0 and 1 representing the LHS width */
  const [panelWidth, setPanelWidth] = usePersistedExtraWorkbenchState(
    selectedEnv,
    "panelWidth",
    0.5
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const router = useRouter();

  const policySaveSegmentCallback = useSegmentCallback(
    WORKBENCH_INTERACTION_SEGMENT_MESSAGE,
    { interaction: "Save policy" }
  );
  const segmentCallback = useSegmentCallbackV2().bind(
    null,
    WORKBENCH_INTERACTION_SEGMENT_MESSAGE
  );

  const emitErrorMessage = (message: string) => {
    segmentCallback({ interaction: `Error: ${message}` });
    setErrorMessage(message);
    setShowErrorMessage(true);
    setTimeout(() => setShowErrorMessage(false), 10000);
  };

  const { workbenchPolicy, dispatch, undo, redo } =
    usePersistedWorkbenchReducer(
      selectedEnv,
      workbenchPolicyReducer.bind(null, emitErrorMessage, segmentCallback)
    );

  const updatePolicyAction = useEnvironmentAction("update_policy");

  const [_, setPersistedWorkbenchState] =
    usePersistedWorkbenchState(selectedEnv);
  const [showRestoreModal, setShowRestoreModal] = useState<boolean>(false);
  const [backendState, setBackendState] = useState<WorkbenchPolicy | null>(
    null
  );

  useEffect(() => {
    if (fetchFromBackend && isBackendStateLoading) {
      // when the page loads:
      // fetch the persisted workbench state
      // compare the policy it would generate with the policy the localstorage state would generate
      // if they're different, show a warning
      fetch(`/web/environment/${selectedEnv.pub_id}/api/policy_workbench`)
        .then((r) => (r.ok ? r.json() : Promise.reject(r)))
        .then((backendWorkbenchPolicy: { policy?: WorkbenchPolicy }) => {
          if (
            backendWorkbenchPolicy.policy &&
            workbenchToPolicy(backendWorkbenchPolicy.policy) !==
              workbenchToPolicy(workbenchPolicy)
          ) {
            setBackendState(backendWorkbenchPolicy.policy);
            setShowRestoreModal(true);
            setIsBackendStateLoading(false);
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }, [selectedEnv, fetchFromBackend, isBackendStateLoading]);

  useEffect(() => {
    const undoRedoListener = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey) {
        if (event.key === "z") undo?.();
        if (event.key === "y") redo?.();
      }
    };
    document.addEventListener("keydown", undoRedoListener);
    return () => document.removeEventListener("keydown", undoRedoListener);
  });

  const { data: existingPolicy, refresh: refreshExistingPolicy } = useData<{
    policy: { src: string } | null;
  }>(() => {
    if (selectedEnv === null) {
      return null;
    }
    return `/web/environment/${selectedEnv.pub_id}/api/policy`;
  });
  const existingPolicyIsEmpty = !existingPolicy || !existingPolicy.policy;

  const policy = workbenchToPolicy(workbenchPolicy);

  const unsavedChanges = existingPolicy?.policy?.src !== policy;
  // pass this up to the parent component so we can set the page title
  useEffect(() => {
    onUnsavedChangesChange(unsavedChanges);
  }, [unsavedChanges]);

  const TimedVisible: FC<{
    delay: number | undefined;
    diagnostic: JSX.Element;
    onTimerEnd: () => void;
  }> = ({ delay, diagnostic, onTimerEnd }) => {
    const [visible, setVisible] = useState(true);

    useEffect(() => {
      const timerId = setTimeout(() => {
        onTimerEnd();
        setVisible(false);
      }, delay);

      return () => {
        clearTimeout(timerId);
      };
    }, [delay]);

    return visible ? <div className="bg-white">{diagnostic}</div> : <div />;
  };

  enum SavingState {
    Default,
    Saving,
    SavingFailure,
    SavingSuccess,
    SavingSuccessExisting,
  }
  const [savingState, setSavingState] = useState<SavingState>(
    SavingState.Default
  );

  const savePolicy = async (remainOnPage?: boolean) => {
    policySaveSegmentCallback();
    remainOnPage = remainOnPage === undefined ? false : remainOnPage;
    if (!selectedEnv) return;
    try {
      const resp = await fetch(
        `/web/environment/${selectedEnv.pub_id}/api/policy`,
        {
          method: "POST",
          body: JSON.stringify({
            src: policy,
          }),
        }
      );
      if (resp.ok) {
        if (resp.status === 202) {
          setSavingState(SavingState.SavingSuccessExisting);
        } else {
          setSavingState(SavingState.SavingSuccess);
        }
        if (!remainOnPage) {
          router.push("/policy");
        }
      } else {
        setSavingState(SavingState.SavingFailure);
        throw resp.body;
      }
    } catch (e) {
      console.error(`Error while saving policy: ${e}`);
    } finally {
      refreshExistingPolicy();
    }
  };

  var diagnostic = <></>;
  switch (savingState) {
    case SavingState.SavingSuccess:
      diagnostic = (
        <span className="mr-4 text-green-500">
          Policy deployed successfully.
        </span>
      );
      break;
    case SavingState.SavingSuccessExisting:
      diagnostic = (
        <span className="mr-4 text-yellow-500">
          Policy appears to be unchanged. Saved policy not overwritten.
        </span>
      );
      break;
    case SavingState.SavingFailure:
      diagnostic = (
        <span className="mr-4 text-red-500">
          Failed to save policy. Fix underlined errors before saving.
        </span>
      );
      break;

    default:
      break;
  }

  return (
    <WizardFlowProvider>
      <div className="relative h-full mx-4 overflow-hidden" ref={containerRef}>
        <div className="flex justify-between mb-2 absolute top-0 inset-x-0 pt-7 h-20 border-b z-10 items-start">
          <h1 className="font-space text-3xl">{header ?? "Rules Workbench"}</h1>
          <div className="flex gap-4">
            <TimedVisible
              delay={5000}
              diagnostic={diagnostic}
              onTimerEnd={() => setSavingState(SavingState.Default)}
            ></TimedVisible>
            {!hideWhenWouldIUse && (
              <>
                <Button
                  medium
                  icon={<HiOutlineQuestionMarkCircle />}
                  onClick={() => {
                    setLimitationsOpen(true);
                  }}
                >
                  {"When Would I Use This?"}
                </Button>
                <div className="border" />
              </>
            )}
            <Button
              medium
              theme="primary"
              icon={<AiOutlineSave />}
              onClick={() => {
                if (existingPolicyIsEmpty) {
                  savePolicy(true);
                } else {
                  setShowPolicyOverwriteWarning(true);
                }
              }}
              disabled={!updatePolicyAction}
            >
              Deploy
            </Button>

            <Button
              medium
              theme="default"
              icon={<HiOutlineRefresh />}
              onClick={() => {
                setShowWorkbenchStartOverWarning(true);
              }}
            >
              Reset
            </Button>
            {otherToolbarButtons}
          </div>
        </div>
        <div className="flex absolute inset-0">
          <div
            className="h-full overflow-auto pt-24"
            style={{ width: `${panelWidth * 100}%` }}
            ref={workbenchEditorContainer}
          >
            {
              <WorkbenchEditor
                selectedEnv={selectedEnv}
                workbenchPolicy={workbenchPolicy}
                dispatch={dispatch}
                emitErrorMessage={emitErrorMessage}
              />
            }
          </div>
          <div
            className="w-2 border-x bg-grey-100 cursor-col-resize"
            draggable
            onDrag={(e) => {
              e.preventDefault();
              if (!containerRef.current) return;
              const containerRect =
                containerRef.current.getBoundingClientRect();
              const cursorX = e.pageX - containerRect.left;
              const total = containerRect.width;
              const newPanelWidth = cursorX / total;
              if (
                0.2 < newPanelWidth &&
                newPanelWidth < 0.8 &&
                Math.abs(newPanelWidth - panelWidth) > 0.01
              ) {
                setPanelWidth(newPanelWidth);
              }
            }}
            onDoubleClick={() => setPanelWidth(0.5)}
            title="Drag to resize. Double-click to reset."
          />
          <div
            className="h-screen overflow-auto pt-24"
            style={{ width: `${(1 - panelWidth) * 100}%` }}
            ref={policyContainer}
          >
            <DiffPolicy policy={policy} />
          </div>
        </div>

        {errorShouldMount && (
          <div className="fixed top-4 left-0 right-0 z-40 flex justify-center w-full">
            <div
              className={`bg-white rounded px-4 py-2 shadow text-red-500 transition-all ${
                errorStage === "enter" ? "" : "opacity-0 -translate-y-4"
              }`}
            >
              {errorMessage}
            </div>
          </div>
        )}
        <Modal
          isOpen={showPolicyOverwriteWarning}
          onClose={() => setShowPolicyOverwriteWarning(false)}
          extraWide
          tall
        >
          <div className="">
            <div className="text-3xl font-space">Overwrite Policy?</div>
            <div className="mt-3" />
            Deploying this policy will overwrite your existing policy.
            <div className="mt-3" />
            {existingPolicy?.policy && (
              <details open className="group">
                <summary className="border rounded p-4 group-open:rounded-b-none">
                  Summary of changes
                </summary>
                <div className="max-h-[65vh] overflow-auto border rounded border-t-0 rounded-t-none">
                  <StaticDiffPolicy
                    newPolicy={policy}
                    oldPolicy={existingPolicy.policy.src}
                  />
                </div>
              </details>
            )}
            <div className="flex p-6 pb-0 justify-around">
              <Button
                medium
                icon={<AiOutlineClose />}
                onClick={() => setShowPolicyOverwriteWarning(false)}
              >
                Cancel
              </Button>
              <Button
                medium
                theme="primary"
                icon={<AiOutlineSave />}
                loaderOnClick
                onClick={() => {
                  savePolicy(true);
                  setShowPolicyOverwriteWarning(false);
                }}
              >
                Deploy Policy
              </Button>
            </div>
          </div>
        </Modal>
        <Modal
          isOpen={showWorkbenchStartOverWarning}
          onClose={() => setShowWorkbenchStartOverWarning(false)}
        >
          <div className="text-lg">Start over?</div>
          <div className="mt-3">
            This will clear everything currently defined in the workbench and
            start over from scratch.
          </div>
          <div className="flex p-4 justify-around">
            <Button
              medium
              icon={<AiOutlineClose />}
              onClick={() => setShowWorkbenchStartOverWarning(false)}
            >
              Cancel
            </Button>
            <Button
              medium
              theme="delete"
              icon={<HiOutlineRefresh />}
              loaderOnClick
              onClick={() => {
                dispatch({ action: "reset" });
                setShowWorkbenchStartOverWarning(false);
              }}
            >
              Clear Rules Workbench and Start Over
            </Button>
          </div>
        </Modal>
        <Modal
          isOpen={limitationsOpen}
          onClose={() => setLimitationsOpen(false)}
          showCloseButton
        >
          <div className="font-space text-3xl">When Would I Use This?</div>
          <div className="mt-4" />

          <div className="flex flex-col space-y-2">
            <div>
              The Rules Workbench is ideal for scaffolding out your initial
              policy and getting familiar with the Polar language for
              authorization.
            </div>
            <div>
              Once you are ready to go to production or collaborate with others,
              you should transition to the{" "}
              <span className="underline text-blue-600">
                <Link href="/policy">Rules Editor</Link>
              </span>
              .
            </div>
          </div>
        </Modal>
        <Modal
          tall
          wide
          isOpen={showRestoreModal}
          onClose={() => setShowRestoreModal(false)}
        >
          {backendState && ( // INVARIANT: do not open this modal unless backendState is not-null
            <>
              <div className="text-3xl font-space">
                Restore undeployed changes?
              </div>
              <div className="mt-4" />
              <div>
                You left the rules editor without deploying your changes.
              </div>
              <div className="mt-2" />
              <div>
                You can resume editing these changes, or you can discard them.
              </div>
              <div className="mt-2" />
              <details open className="group">
                <summary className="border rounded p-4 group-open:rounded-b-none">
                  Summary of your undeployed changes
                </summary>
                <div className="max-h-[65vh] overflow-auto border rounded border-t-0 rounded-t-none">
                  <StaticDiffPolicy
                    newPolicy={policy}
                    oldPolicy={workbenchToPolicy(backendState)}
                  />
                </div>
              </details>
              <div className="mt-4" />
              <div className="flex justify-around">
                <Button
                  medium
                  onClick={() => {
                    setPersistedWorkbenchState(backendState);
                    setShowRestoreModal(false);
                  }}
                >
                  Discard these changes
                </Button>
                <Button medium onClick={() => setShowRestoreModal(false)}>
                  Keep editing these changes
                </Button>
              </div>
            </>
          )}
        </Modal>
      </div>
      <Wizard
        workbenchPolicy={workbenchPolicy}
        dispatch={dispatch}
        selectedEnv={selectedEnv}
        listenForScroll={[workbenchEditorContainer, policyContainer]}
      />
    </WizardFlowProvider>
  );
};

export default Workbench;
